import { AxiosResponse } from 'axios'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import type Stripe from 'stripe'
import { $axios } from './axios'

export type EarlyAccessResponse = {
  earlyAccess: boolean
}

export type CustomerPortalResponse = {
  url: string
}

export type PaymentSessionResponse = {
  clientSecret: string
  publishableKey: string
  pricingTableId: string
}

export type ActiveSubscriptionResponse = {
  subscription: Stripe.Subscription | null
}

export const getEarlyAccess = (userId: string): Promise<AxiosResponse<EarlyAccessResponse>> => {
  return $axios.get('/early-access', {
    params: { userId }
  })
}

export const createCustomerPortal = (): Promise<AxiosResponse<CustomerPortalResponse>> => {
  return $axios.post('/customer-portal')
}

export const createPaymentSession = (inboxIds?: string[]): Promise<AxiosResponse<PaymentSessionResponse>> => {
  return $axios.post('/payment-session', { inboxIds })
}

export const getActiveSubscription = (): Promise<AxiosResponse<ActiveSubscriptionResponse>> => {
  return $axios.get('/subscription')
}

export const useEarlyAccessQuery = (
  userId: string,
  options?: Omit<UseQueryOptions<EarlyAccessResponse, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<EarlyAccessResponse, Error>({
    queryKey: ['earlyAccess', userId],
    queryFn: async () => {
      const res = await getEarlyAccess(userId)
      return res.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!userId, // Only run if userId is provided
    ...options
  })
}

export const useActiveSubscriptionQuery = (
  authUserId: string | undefined,
  options?: Omit<UseQueryOptions<ActiveSubscriptionResponse, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<ActiveSubscriptionResponse, Error>({
    queryKey: ['activeSubscription', authUserId],
    queryFn: async () => {
      const res = await getActiveSubscription()
      return res.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!authUserId, // Only fetch if authUser is valid
    ...options
  })
}
